import { cn } from '@/utils/cn'

interface SelectableCardProps {
  selected: boolean
  onSelect: () => void
  icon?: React.ReactNode
  title?: string
  description?: string
  children?: React.ReactNode
  variant?: 'vertical' | 'horizontal' | 'compact'
  showCheckbox?: boolean
  className?: string
  /** Use `radio` when the card is one of a mutually exclusive set inside a `radiogroup`. */
  role?: 'button' | 'radio'
}

/**
 * A reusable selectable card component with keyboard accessibility.
 * Used for selection interfaces like method cards, plan type cards, season cards, etc.
 *
 * Variants:
 * - vertical: Icon on top, title and description below (default)
 * - horizontal: Icon and text side by side
 * - compact: Centered content, no checkbox (useful for simple selections like years)
 */
export function SelectableCard({
  selected,
  onSelect,
  icon,
  title,
  description,
  children,
  variant = 'vertical',
  showCheckbox = true,
  className,
  role = 'button',
}: SelectableCardProps) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onSelect()
    }
  }

  // Base card styles
  const baseStyles = cn(
    'relative rounded-[12px] border-2 transition-all cursor-pointer',
    'shadow-[0px_1px_2px_0px_rgba(0,0,0,0.09)]',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    selected
      ? 'bg-[#FEF2F3] border-[#CF1F2E]'
      : 'bg-white border-[#D6DAE1] hover:border-[#CF1F2E]/50'
  )

  // Icon container styles
  const iconContainerStyles = cn(
    'flex items-center justify-center rounded-[10px] shrink-0',
    variant === 'compact' ? 'size-12' : 'h-[48px] w-[48px] p-[12px]',
    selected
      ? variant === 'horizontal'
        ? 'bg-[#FEF2F3]'
        : 'bg-[#CF1F2E] text-white'
      : 'bg-[#F8F8F8] text-[#556374]'
  )

  // Horizontal icon variant has different selected style
  const horizontalIconStyles = cn(
    'flex items-center justify-center rounded-[10px] size-12 shrink-0',
    selected ? 'bg-[#FEF2F3]' : 'bg-[#f8f8f8]'
  )

  // Title styles
  const titleStyles = cn(
    variant === 'horizontal' ? 'text-[16px]' : 'text-[18px]',
    'font-semibold leading-[28px]',
    selected ? 'text-[#CF1F2E]' : 'text-[#1C1C1C]'
  )

  // Checkbox component
  const Checkbox = showCheckbox && variant !== 'compact' && (
    <div
      className={cn(
        'h-[16px] w-[16px] rounded-full border flex items-center justify-center shrink-0',
        selected ? 'bg-[#CF1F2E] border-[#9B1924]' : 'bg-white border-[#D6DAE1]'
      )}
    >
      {selected && <div className="h-3 w-3 rounded-full bg-white" />}
    </div>
  )

  // Compact variant (for year cards, etc.)
  if (variant === 'compact') {
    return (
      <div
        role={role}
        aria-checked={role === 'radio' ? selected : undefined}
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={handleKeyDown}
        className={cn(
          baseStyles,
          'flex items-center justify-center p-[24px] min-w-[80px]',
          className
        )}
      >
        {children || (
          <span
            className={cn(
              'text-[18px] font-semibold',
              selected ? 'text-[#CF1F2E]' : 'text-[#1C1C1C]'
            )}
          >
            {title}
          </span>
        )}
      </div>
    )
  }

  // Horizontal variant (for season cards, etc.)
  if (variant === 'horizontal') {
    return (
      <div
        role={role}
        aria-checked={role === 'radio' ? selected : undefined}
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={handleKeyDown}
        className={cn(
          baseStyles,
          'flex gap-3 items-center p-[24px] min-w-[140px]',
          className
        )}
      >
        {icon && (
          <div className={horizontalIconStyles}>
            <div
              className={cn(
                'size-6',
                selected ? 'text-[#CF1F2E]' : 'text-[#556374]'
              )}
            >
              {icon}
            </div>
          </div>
        )}
        {children || (
          <span
            className={cn(
              'text-[16px] font-semibold leading-[20px]',
              selected ? 'text-[#CF1F2E]' : 'text-[#1C1C1C]'
            )}
          >
            {title}
          </span>
        )}
      </div>
    )
  }

  // Default vertical variant (for method cards, plan type cards)
  return (
    <div
      role={role}
      aria-checked={role === 'radio' ? selected : undefined}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      className={cn(baseStyles, 'flex flex-col gap-[16px] p-[24px]', className)}
    >
      {children || (
        <>
          {/* Icon and checkbox container */}
          <div className="flex items-start justify-between w-full">
            {icon && <div className={iconContainerStyles}>{icon}</div>}
            {Checkbox}
          </div>

          {/* Title */}
          {title && <h3 className={titleStyles}>{title}</h3>}

          {/* Description */}
          {description && (
            <p className="text-[14px] leading-[22.75px] text-[#556374]">
              {description}
            </p>
          )}
        </>
      )}
    </div>
  )
}
